// Full set of Azure Cognitive Services standard Neural voices ($15 / 1M
// characters tier) that Readlang exposes in the unified voice picker. Keyed
// by Readlang language code (matching Google Translate codes — e.g. "iw" not
// "he"; "no" not "nb"; "tl" rather than "fil").
//
// Shared between the backend (voiceId validation + default-voice lookup in
// src/msSpeech.js) and the frontend (listVoices in public/src/tts/azureProvider).
//
// **Single source of truth.** A language with no entries here is treated as
// unsupported by Azure on both sides — server gating, picker visibility,
// `canUseAzure` checks. The **first entry** for each language is the default
// voice the server uses when no voiceId is supplied (subject to the regional
// timezone overrides for `es` and `pt` in `src/msSpeech.js`).
//
// Excluded by design: Neural HD / Dragon HD voices (~$30/1M tier),
// Multilingual voices (different feature set), child voices, and preview-
// only experimental voices.

/**
 * @typedef {{
 *   id: string;           // Azure voice name, e.g. "sv-SE-MattiasNeural"
 *   displayName: string;  // Friendly label for the picker
 *   gender: "male" | "female";
 *   latinScriptId?: string; // Same speaker, Latin-script locale
 * }} AzureVoice
 *
 * `latinScriptId` is only set for languages Azure splits by script (Serbian).
 * It never appears in the picker — it's the same voice, not a separate
 * choice; the server swaps to it when the text being synthesized is Latin
 * (see `voiceForTextScript` in src/msSpeech.js).
 */

/** @type {{ [language: string]: AzureVoice[] }} */
export const azureVoicesByLanguage = {
  sv: [
    { id: "sv-SE-MattiasNeural", displayName: "Mattias", gender: "male" },
    { id: "sv-SE-SofieNeural", displayName: "Sofie", gender: "female" },
    { id: "sv-SE-HilleviNeural", displayName: "Hillevi", gender: "female" },
  ],
  en: [
    { id: "en-US-JennyNeural", displayName: "Jenny (US)", gender: "female" },
    { id: "en-US-GuyNeural", displayName: "Guy (US)", gender: "male" },
    { id: "en-US-AvaNeural", displayName: "Ava (US)", gender: "female" },
    { id: "en-US-AndrewNeural", displayName: "Andrew (US)", gender: "male" },
    { id: "en-US-EmmaNeural", displayName: "Emma (US)", gender: "female" },
    { id: "en-US-BrianNeural", displayName: "Brian (US)", gender: "male" },
    { id: "en-US-AriaNeural", displayName: "Aria (US)", gender: "female" },
    { id: "en-US-DavisNeural", displayName: "Davis (US)", gender: "male" },
    { id: "en-US-JaneNeural", displayName: "Jane (US)", gender: "female" },
    { id: "en-US-JasonNeural", displayName: "Jason (US)", gender: "male" },
    { id: "en-US-KaiNeural", displayName: "Kai (US)", gender: "male" },
    { id: "en-US-LunaNeural", displayName: "Luna (US)", gender: "female" },
    { id: "en-US-SaraNeural", displayName: "Sara (US)", gender: "female" },
    { id: "en-US-TonyNeural", displayName: "Tony (US)", gender: "male" },
    { id: "en-US-NancyNeural", displayName: "Nancy (US)", gender: "female" },
    { id: "en-US-AmberNeural", displayName: "Amber (US)", gender: "female" },
    { id: "en-US-AshleyNeural", displayName: "Ashley (US)", gender: "female" },
    {
      id: "en-US-BrandonNeural",
      displayName: "Brandon (US)",
      gender: "male",
    },
    {
      id: "en-US-ChristopherNeural",
      displayName: "Christopher (US)",
      gender: "male",
    },
    { id: "en-US-CoraNeural", displayName: "Cora (US)", gender: "female" },
    {
      id: "en-US-ElizabethNeural",
      displayName: "Elizabeth (US)",
      gender: "female",
    },
    { id: "en-US-EricNeural", displayName: "Eric (US)", gender: "male" },
    { id: "en-US-JacobNeural", displayName: "Jacob (US)", gender: "male" },
    {
      id: "en-US-MichelleNeural",
      displayName: "Michelle (US)",
      gender: "female",
    },
    { id: "en-US-MonicaNeural", displayName: "Monica (US)", gender: "female" },
    { id: "en-US-RogerNeural", displayName: "Roger (US)", gender: "male" },
    { id: "en-US-SteffanNeural", displayName: "Steffan (US)", gender: "male" },
    { id: "en-GB-SoniaNeural", displayName: "Sonia (UK)", gender: "female" },
    { id: "en-GB-RyanNeural", displayName: "Ryan (UK)", gender: "male" },
    { id: "en-GB-LibbyNeural", displayName: "Libby (UK)", gender: "female" },
    { id: "en-GB-AbbiNeural", displayName: "Abbi (UK)", gender: "female" },
    { id: "en-GB-AlfieNeural", displayName: "Alfie (UK)", gender: "male" },
    { id: "en-GB-BellaNeural", displayName: "Bella (UK)", gender: "female" },
    { id: "en-GB-ElliotNeural", displayName: "Elliot (UK)", gender: "male" },
    { id: "en-GB-EthanNeural", displayName: "Ethan (UK)", gender: "male" },
    { id: "en-GB-HollieNeural", displayName: "Hollie (UK)", gender: "female" },
    { id: "en-GB-NoahNeural", displayName: "Noah (UK)", gender: "male" },
    { id: "en-GB-OliverNeural", displayName: "Oliver (UK)", gender: "male" },
    { id: "en-GB-OliviaNeural", displayName: "Olivia (UK)", gender: "female" },
    { id: "en-GB-ThomasNeural", displayName: "Thomas (UK)", gender: "male" },
    {
      id: "en-AU-NatashaNeural",
      displayName: "Natasha (AU)",
      gender: "female",
    },
    { id: "en-AU-WilliamNeural", displayName: "William (AU)", gender: "male" },
    {
      id: "en-AU-AnnetteNeural",
      displayName: "Annette (AU)",
      gender: "female",
    },
    { id: "en-AU-CarlyNeural", displayName: "Carly (AU)", gender: "female" },
    { id: "en-AU-DarrenNeural", displayName: "Darren (AU)", gender: "male" },
    { id: "en-AU-DuncanNeural", displayName: "Duncan (AU)", gender: "male" },
    { id: "en-AU-ElsieNeural", displayName: "Elsie (AU)", gender: "female" },
    { id: "en-AU-FreyaNeural", displayName: "Freya (AU)", gender: "female" },
    { id: "en-AU-JoanneNeural", displayName: "Joanne (AU)", gender: "female" },
    { id: "en-AU-KenNeural", displayName: "Ken (AU)", gender: "male" },
    { id: "en-AU-KimNeural", displayName: "Kim (AU)", gender: "female" },
    { id: "en-AU-NeilNeural", displayName: "Neil (AU)", gender: "male" },
    { id: "en-AU-TimNeural", displayName: "Tim (AU)", gender: "male" },
    { id: "en-AU-TinaNeural", displayName: "Tina (AU)", gender: "female" },
    {
      id: "en-CA-ClaraNeural",
      displayName: "Clara (Canada)",
      gender: "female",
    },
    { id: "en-CA-LiamNeural", displayName: "Liam (Canada)", gender: "male" },
    {
      id: "en-IE-EmilyNeural",
      displayName: "Emily (Ireland)",
      gender: "female",
    },
    {
      id: "en-IE-ConnorNeural",
      displayName: "Connor (Ireland)",
      gender: "male",
    },
    {
      id: "en-NZ-MollyNeural",
      displayName: "Molly (New Zealand)",
      gender: "female",
    },
    {
      id: "en-NZ-MitchellNeural",
      displayName: "Mitchell (New Zealand)",
      gender: "male",
    },
    {
      id: "en-IN-NeerjaNeural",
      displayName: "Neerja (India)",
      gender: "female",
    },
    {
      id: "en-IN-PrabhatNeural",
      displayName: "Prabhat (India)",
      gender: "male",
    },
    { id: "en-IN-AaravNeural", displayName: "Aarav (India)", gender: "male" },
    { id: "en-IN-AashiNeural", displayName: "Aashi (India)", gender: "female" },
    { id: "en-IN-AartiNeural", displayName: "Aarti (India)", gender: "female" },
    { id: "en-IN-ArjunNeural", displayName: "Arjun (India)", gender: "male" },
    {
      id: "en-IN-AnanyaNeural",
      displayName: "Ananya (India)",
      gender: "female",
    },
    { id: "en-IN-KavyaNeural", displayName: "Kavya (India)", gender: "female" },
    { id: "en-IN-KunalNeural", displayName: "Kunal (India)", gender: "male" },
    { id: "en-IN-RehaanNeural", displayName: "Rehaan (India)", gender: "male" },
    {
      id: "en-ZA-LeahNeural",
      displayName: "Leah (South Africa)",
      gender: "female",
    },
    {
      id: "en-ZA-LukeNeural",
      displayName: "Luke (South Africa)",
      gender: "male",
    },
    {
      id: "en-HK-YanNeural",
      displayName: "Yan (Hong Kong)",
      gender: "female",
    },
    { id: "en-HK-SamNeural", displayName: "Sam (Hong Kong)", gender: "male" },
    {
      id: "en-SG-LunaNeural",
      displayName: "Luna (Singapore)",
      gender: "female",
    },
    {
      id: "en-SG-WayneNeural",
      displayName: "Wayne (Singapore)",
      gender: "male",
    },
    {
      id: "en-PH-RosaNeural",
      displayName: "Rosa (Philippines)",
      gender: "female",
    },
    {
      id: "en-PH-JamesNeural",
      displayName: "James (Philippines)",
      gender: "male",
    },
    {
      id: "en-NG-EzinneNeural",
      displayName: "Ezinne (Nigeria)",
      gender: "female",
    },
    { id: "en-NG-AbeoNeural", displayName: "Abeo (Nigeria)", gender: "male" },
    {
      id: "en-KE-AsiliaNeural",
      displayName: "Asilia (Kenya)",
      gender: "female",
    },
    {
      id: "en-KE-ChilembaNeural",
      displayName: "Chilemba (Kenya)",
      gender: "male",
    },
    {
      id: "en-TZ-ImaniNeural",
      displayName: "Imani (Tanzania)",
      gender: "female",
    },
    {
      id: "en-TZ-ElimuNeural",
      displayName: "Elimu (Tanzania)",
      gender: "male",
    },
  ],
  es: [
    { id: "es-ES-AbrilNeural", displayName: "Abril (Spain)", gender: "female" },
    { id: "es-ES-AlvaroNeural", displayName: "Álvaro (Spain)", gender: "male" },
    {
      id: "es-ES-ElviraNeural",
      displayName: "Elvira (Spain)",
      gender: "female",
    },
    { id: "es-ES-ArnauNeural", displayName: "Arnau (Spain)", gender: "male" },
    { id: "es-ES-DarioNeural", displayName: "Darío (Spain)", gender: "male" },
    { id: "es-ES-EliasNeural", displayName: "Elías (Spain)", gender: "male" },
    {
      id: "es-ES-EstrellaNeural",
      displayName: "Estrella (Spain)",
      gender: "female",
    },
    { id: "es-ES-IreneNeural", displayName: "Irene (Spain)", gender: "female" },
    { id: "es-ES-LaiaNeural", displayName: "Laia (Spain)", gender: "female" },
    { id: "es-ES-LiaNeural", displayName: "Lía (Spain)", gender: "female" },
    { id: "es-ES-NilNeural", displayName: "Nil (Spain)", gender: "male" },
    { id: "es-ES-SaulNeural", displayName: "Saúl (Spain)", gender: "male" },
    { id: "es-ES-TeoNeural", displayName: "Teo (Spain)", gender: "male" },
    {
      id: "es-ES-TrianaNeural",
      displayName: "Triana (Spain)",
      gender: "female",
    },
    { id: "es-ES-VeraNeural", displayName: "Vera (Spain)", gender: "female" },
    {
      id: "es-ES-XimenaNeural",
      displayName: "Ximena (Spain)",
      gender: "female",
    },
    {
      id: "es-MX-DaliaNeural",
      displayName: "Dalia (Mexico)",
      gender: "female",
    },
    { id: "es-MX-JorgeNeural", displayName: "Jorge (Mexico)", gender: "male" },
    {
      id: "es-MX-BeatrizNeural",
      displayName: "Beatriz (Mexico)",
      gender: "female",
    },
    {
      id: "es-MX-CandelaNeural",
      displayName: "Candela (Mexico)",
      gender: "female",
    },
    {
      id: "es-MX-CarlotaNeural",
      displayName: "Carlota (Mexico)",
      gender: "female",
    },
    {
      id: "es-MX-CecilioNeural",
      displayName: "Cecilio (Mexico)",
      gender: "male",
    },
    {
      id: "es-MX-GerardoNeural",
      displayName: "Gerardo (Mexico)",
      gender: "male",
    },
    {
      id: "es-MX-LarissaNeural",
      displayName: "Larissa (Mexico)",
      gender: "female",
    },
    {
      id: "es-MX-LibertoNeural",
      displayName: "Liberto (Mexico)",
      gender: "male",
    },
    {
      id: "es-MX-LucianoNeural",
      displayName: "Luciano (Mexico)",
      gender: "male",
    },
    {
      id: "es-MX-NuriaNeural",
      displayName: "Nuria (Mexico)",
      gender: "female",
    },
    {
      id: "es-MX-PelayoNeural",
      displayName: "Pelayo (Mexico)",
      gender: "male",
    },
    {
      id: "es-MX-RenataNeural",
      displayName: "Renata (Mexico)",
      gender: "female",
    },
    { id: "es-MX-YagoNeural", displayName: "Yago (Mexico)", gender: "male" },
    {
      id: "es-AR-ElenaNeural",
      displayName: "Elena (Argentina)",
      gender: "female",
    },
    {
      id: "es-AR-TomasNeural",
      displayName: "Tomás (Argentina)",
      gender: "male",
    },
    {
      id: "es-CO-SalomeNeural",
      displayName: "Salomé (Colombia)",
      gender: "female",
    },
    {
      id: "es-CO-GonzaloNeural",
      displayName: "Gonzalo (Colombia)",
      gender: "male",
    },
    {
      id: "es-CL-CatalinaNeural",
      displayName: "Catalina (Chile)",
      gender: "female",
    },
    {
      id: "es-CL-LorenzoNeural",
      displayName: "Lorenzo (Chile)",
      gender: "male",
    },
    {
      id: "es-PE-CamilaNeural",
      displayName: "Camila (Peru)",
      gender: "female",
    },
    { id: "es-PE-AlexNeural", displayName: "Alex (Peru)", gender: "male" },
    {
      id: "es-VE-PaolaNeural",
      displayName: "Paola (Venezuela)",
      gender: "female",
    },
    {
      id: "es-VE-SebastianNeural",
      displayName: "Sebastián (Venezuela)",
      gender: "male",
    },
    {
      id: "es-EC-AndreaNeural",
      displayName: "Andrea (Ecuador)",
      gender: "female",
    },
    { id: "es-EC-LuisNeural", displayName: "Luis (Ecuador)", gender: "male" },
    {
      id: "es-BO-SofiaNeural",
      displayName: "Sofía (Bolivia)",
      gender: "female",
    },
    {
      id: "es-BO-MarceloNeural",
      displayName: "Marcelo (Bolivia)",
      gender: "male",
    },
    {
      id: "es-US-PalomaNeural",
      displayName: "Paloma (US)",
      gender: "female",
    },
    { id: "es-US-AlonsoNeural", displayName: "Alonso (US)", gender: "male" },
    {
      id: "es-UY-ValentinaNeural",
      displayName: "Valentina (Uruguay)",
      gender: "female",
    },
    {
      id: "es-UY-MateoNeural",
      displayName: "Mateo (Uruguay)",
      gender: "male",
    },
    {
      id: "es-PY-TaniaNeural",
      displayName: "Tania (Paraguay)",
      gender: "female",
    },
    {
      id: "es-PY-MarioNeural",
      displayName: "Mario (Paraguay)",
      gender: "male",
    },
    {
      id: "es-CU-BelkysNeural",
      displayName: "Belkys (Cuba)",
      gender: "female",
    },
    { id: "es-CU-ManuelNeural", displayName: "Manuel (Cuba)", gender: "male" },
    {
      id: "es-DO-RamonaNeural",
      displayName: "Ramona (Dominican Republic)",
      gender: "female",
    },
    {
      id: "es-DO-EmilioNeural",
      displayName: "Emilio (Dominican Republic)",
      gender: "male",
    },
    {
      id: "es-PR-KarinaNeural",
      displayName: "Karina (Puerto Rico)",
      gender: "female",
    },
    {
      id: "es-PR-VictorNeural",
      displayName: "Víctor (Puerto Rico)",
      gender: "male",
    },
    {
      id: "es-CR-MariaNeural",
      displayName: "María (Costa Rica)",
      gender: "female",
    },
    {
      id: "es-CR-JuanNeural",
      displayName: "Juan (Costa Rica)",
      gender: "male",
    },
    {
      id: "es-PA-MargaritaNeural",
      displayName: "Margarita (Panama)",
      gender: "female",
    },
    {
      id: "es-PA-RobertoNeural",
      displayName: "Roberto (Panama)",
      gender: "male",
    },
    {
      id: "es-NI-YolandaNeural",
      displayName: "Yolanda (Nicaragua)",
      gender: "female",
    },
    {
      id: "es-NI-FedericoNeural",
      displayName: "Federico (Nicaragua)",
      gender: "male",
    },
    {
      id: "es-HN-KarlaNeural",
      displayName: "Karla (Honduras)",
      gender: "female",
    },
    {
      id: "es-HN-CarlosNeural",
      displayName: "Carlos (Honduras)",
      gender: "male",
    },
    {
      id: "es-GT-MartaNeural",
      displayName: "Marta (Guatemala)",
      gender: "female",
    },
    {
      id: "es-GT-AndresNeural",
      displayName: "Andrés (Guatemala)",
      gender: "male",
    },
    {
      id: "es-SV-LorenaNeural",
      displayName: "Lorena (El Salvador)",
      gender: "female",
    },
    {
      id: "es-SV-RodrigoNeural",
      displayName: "Rodrigo (El Salvador)",
      gender: "male",
    },
    {
      id: "es-GQ-TeresaNeural",
      displayName: "Teresa (Equatorial Guinea)",
      gender: "female",
    },
    {
      id: "es-GQ-JavierNeural",
      displayName: "Javier (Equatorial Guinea)",
      gender: "male",
    },
  ],
  fr: [
    {
      id: "fr-FR-BrigitteNeural",
      displayName: "Brigitte (France)",
      gender: "female",
    },
    { id: "fr-FR-HenriNeural", displayName: "Henri (France)", gender: "male" },
    {
      id: "fr-FR-DeniseNeural",
      displayName: "Denise (France)",
      gender: "female",
    },
    { id: "fr-FR-AlainNeural", displayName: "Alain (France)", gender: "male" },
    {
      id: "fr-FR-CelesteNeural",
      displayName: "Céleste (France)",
      gender: "female",
    },
    {
      id: "fr-FR-ClaudeNeural",
      displayName: "Claude (France)",
      gender: "male",
    },
    {
      id: "fr-FR-CoralieNeural",
      displayName: "Coralie (France)",
      gender: "female",
    },
    {
      id: "fr-FR-JacquelineNeural",
      displayName: "Jacqueline (France)",
      gender: "female",
    },
    {
      id: "fr-FR-JeromeNeural",
      displayName: "Jérôme (France)",
      gender: "male",
    },
    {
      id: "fr-FR-JosephineNeural",
      displayName: "Joséphine (France)",
      gender: "female",
    },
    {
      id: "fr-FR-MauriceNeural",
      displayName: "Maurice (France)",
      gender: "male",
    },
    { id: "fr-FR-YvesNeural", displayName: "Yves (France)", gender: "male" },
    {
      id: "fr-FR-YvetteNeural",
      displayName: "Yvette (France)",
      gender: "female",
    },
    {
      id: "fr-CA-SylvieNeural",
      displayName: "Sylvie (Canada)",
      gender: "female",
    },
    { id: "fr-CA-JeanNeural", displayName: "Jean (Canada)", gender: "male" },
    {
      id: "fr-CA-AntoineNeural",
      displayName: "Antoine (Canada)",
      gender: "male",
    },
    {
      id: "fr-CA-ThierryNeural",
      displayName: "Thierry (Canada)",
      gender: "male",
    },
    {
      id: "fr-BE-CharlineNeural",
      displayName: "Charline (Belgium)",
      gender: "female",
    },
    {
      id: "fr-BE-GerardNeural",
      displayName: "Gérard (Belgium)",
      gender: "male",
    },
    {
      id: "fr-CH-ArianeNeural",
      displayName: "Ariane (Switzerland)",
      gender: "female",
    },
    {
      id: "fr-CH-FabriceNeural",
      displayName: "Fabrice (Switzerland)",
      gender: "male",
    },
  ],
  de: [
    {
      id: "de-DE-AmalaNeural",
      displayName: "Amala (Germany)",
      gender: "female",
    },
    {
      id: "de-DE-ConradNeural",
      displayName: "Conrad (Germany)",
      gender: "male",
    },
    {
      id: "de-DE-KatjaNeural",
      displayName: "Katja (Germany)",
      gender: "female",
    },
    { id: "de-DE-BerndNeural", displayName: "Bernd (Germany)", gender: "male" },
    {
      id: "de-DE-ChristophNeural",
      displayName: "Christoph (Germany)",
      gender: "male",
    },
    { id: "de-DE-ElkeNeural", displayName: "Elke (Germany)", gender: "female" },
    {
      id: "de-DE-KasperNeural",
      displayName: "Kasper (Germany)",
      gender: "male",
    },
    {
      id: "de-DE-KillianNeural",
      displayName: "Killian (Germany)",
      gender: "male",
    },
    {
      id: "de-DE-KlarissaNeural",
      displayName: "Klarissa (Germany)",
      gender: "female",
    },
    { id: "de-DE-KlausNeural", displayName: "Klaus (Germany)", gender: "male" },
    {
      id: "de-DE-LouisaNeural",
      displayName: "Louisa (Germany)",
      gender: "female",
    },
    { id: "de-DE-MajaNeural", displayName: "Maja (Germany)", gender: "female" },
    { id: "de-DE-RalfNeural", displayName: "Ralf (Germany)", gender: "male" },
    {
      id: "de-DE-TanjaNeural",
      displayName: "Tanja (Germany)",
      gender: "female",
    },
    {
      id: "de-AT-IngridNeural",
      displayName: "Ingrid (Austria)",
      gender: "female",
    },
    { id: "de-AT-JonasNeural", displayName: "Jonas (Austria)", gender: "male" },
    {
      id: "de-CH-LeniNeural",
      displayName: "Leni (Switzerland)",
      gender: "female",
    },
    { id: "de-CH-JanNeural", displayName: "Jan (Switzerland)", gender: "male" },
  ],
  it: [
    { id: "it-IT-ElsaNeural", displayName: "Elsa", gender: "female" },
    { id: "it-IT-DiegoNeural", displayName: "Diego", gender: "male" },
    { id: "it-IT-IsabellaNeural", displayName: "Isabella", gender: "female" },
    { id: "it-IT-BenignoNeural", displayName: "Benigno", gender: "male" },
    { id: "it-IT-CalimeroNeural", displayName: "Calimero", gender: "male" },
    { id: "it-IT-CataldoNeural", displayName: "Cataldo", gender: "male" },
    { id: "it-IT-FabiolaNeural", displayName: "Fabiola", gender: "female" },
    { id: "it-IT-FiammaNeural", displayName: "Fiamma", gender: "female" },
    { id: "it-IT-GianniNeural", displayName: "Gianni", gender: "male" },
    { id: "it-IT-GiuseppeNeural", displayName: "Giuseppe", gender: "male" },
    { id: "it-IT-ImeldaNeural", displayName: "Imelda", gender: "female" },
    { id: "it-IT-IrmaNeural", displayName: "Irma", gender: "female" },
    { id: "it-IT-LisandroNeural", displayName: "Lisandro", gender: "male" },
    { id: "it-IT-PalmiraNeural", displayName: "Palmira", gender: "female" },
    { id: "it-IT-RinaldoNeural", displayName: "Rinaldo", gender: "male" },
  ],
  pt: [
    {
      id: "pt-BR-FranciscaNeural",
      displayName: "Francisca (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-AntonioNeural",
      displayName: "Antônio (Brazil)",
      gender: "male",
    },
    {
      id: "pt-BR-BrendaNeural",
      displayName: "Brenda (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-DonatoNeural",
      displayName: "Donato (Brazil)",
      gender: "male",
    },
    { id: "pt-BR-ElzaNeural", displayName: "Elza (Brazil)", gender: "female" },
    { id: "pt-BR-FabioNeural", displayName: "Fábio (Brazil)", gender: "male" },
    {
      id: "pt-BR-GiovannaNeural",
      displayName: "Giovanna (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-HumbertoNeural",
      displayName: "Humberto (Brazil)",
      gender: "male",
    },
    { id: "pt-BR-JulioNeural", displayName: "Júlio (Brazil)", gender: "male" },
    {
      id: "pt-BR-LeilaNeural",
      displayName: "Leila (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-ManuelaNeural",
      displayName: "Manuela (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-NicolauNeural",
      displayName: "Nicolau (Brazil)",
      gender: "male",
    },
    {
      id: "pt-BR-ThalitaNeural",
      displayName: "Thalita (Brazil)",
      gender: "female",
    },
    {
      id: "pt-BR-ValerioNeural",
      displayName: "Valério (Brazil)",
      gender: "male",
    },
    { id: "pt-BR-YaraNeural", displayName: "Yara (Brazil)", gender: "female" },
    {
      id: "pt-PT-RaquelNeural",
      displayName: "Raquel (Portugal)",
      gender: "female",
    },
    {
      id: "pt-PT-DuarteNeural",
      displayName: "Duarte (Portugal)",
      gender: "male",
    },
    {
      id: "pt-PT-FernandaNeural",
      displayName: "Fernanda (Portugal)",
      gender: "female",
    },
  ],
  nl: [
    {
      id: "nl-NL-ColetteNeural",
      displayName: "Colette (Netherlands)",
      gender: "female",
    },
    {
      id: "nl-NL-MaartenNeural",
      displayName: "Maarten (Netherlands)",
      gender: "male",
    },
    {
      id: "nl-NL-FennaNeural",
      displayName: "Fenna (Netherlands)",
      gender: "female",
    },
    {
      id: "nl-BE-DenaNeural",
      displayName: "Dena (Belgium)",
      gender: "female",
    },
    {
      id: "nl-BE-ArnaudNeural",
      displayName: "Arnaud (Belgium)",
      gender: "male",
    },
  ],
  ru: [
    { id: "ru-RU-SvetlanaNeural", displayName: "Svetlana", gender: "female" },
    { id: "ru-RU-DmitryNeural", displayName: "Dmitry", gender: "male" },
    { id: "ru-RU-DariyaNeural", displayName: "Dariya", gender: "female" },
  ],
  pl: [
    { id: "pl-PL-AgnieszkaNeural", displayName: "Agnieszka", gender: "female" },
    { id: "pl-PL-MarekNeural", displayName: "Marek", gender: "male" },
    { id: "pl-PL-ZofiaNeural", displayName: "Zofia", gender: "female" },
  ],
  tr: [
    { id: "tr-TR-EmelNeural", displayName: "Emel", gender: "female" },
    { id: "tr-TR-AhmetNeural", displayName: "Ahmet", gender: "male" },
  ],
  ja: [
    { id: "ja-JP-NanamiNeural", displayName: "Nanami", gender: "female" },
    { id: "ja-JP-KeitaNeural", displayName: "Keita", gender: "male" },
    { id: "ja-JP-AoiNeural", displayName: "Aoi", gender: "female" },
    { id: "ja-JP-DaichiNeural", displayName: "Daichi", gender: "male" },
    { id: "ja-JP-MayuNeural", displayName: "Mayu", gender: "female" },
    { id: "ja-JP-NaokiNeural", displayName: "Naoki", gender: "male" },
    { id: "ja-JP-ShioriNeural", displayName: "Shiori", gender: "female" },
  ],
  ko: [
    { id: "ko-KR-SunHiNeural", displayName: "SunHi", gender: "female" },
    { id: "ko-KR-InJoonNeural", displayName: "InJoon", gender: "male" },
    { id: "ko-KR-BongJinNeural", displayName: "BongJin", gender: "male" },
    { id: "ko-KR-GookMinNeural", displayName: "GookMin", gender: "male" },
    { id: "ko-KR-HyunsuNeural", displayName: "Hyunsu", gender: "male" },
    { id: "ko-KR-JiMinNeural", displayName: "JiMin", gender: "female" },
    { id: "ko-KR-SeoHyeonNeural", displayName: "SeoHyeon", gender: "female" },
    { id: "ko-KR-SoonBokNeural", displayName: "SoonBok", gender: "female" },
    { id: "ko-KR-YuJinNeural", displayName: "YuJin", gender: "female" },
  ],
  zh: [
    { id: "zh-CN-XiaoxiaoNeural", displayName: "Xiaoxiao", gender: "female" },
    { id: "zh-CN-YunxiNeural", displayName: "Yunxi", gender: "male" },
    { id: "zh-CN-YunjianNeural", displayName: "Yunjian", gender: "male" },
    { id: "zh-CN-XiaoyiNeural", displayName: "Xiaoyi", gender: "female" },
    { id: "zh-CN-YunyangNeural", displayName: "Yunyang", gender: "male" },
    { id: "zh-CN-XiaochenNeural", displayName: "Xiaochen", gender: "female" },
    { id: "zh-CN-XiaohanNeural", displayName: "Xiaohan", gender: "female" },
    { id: "zh-CN-XiaomengNeural", displayName: "Xiaomeng", gender: "female" },
    { id: "zh-CN-XiaomoNeural", displayName: "Xiaomo", gender: "female" },
    { id: "zh-CN-XiaoqiuNeural", displayName: "Xiaoqiu", gender: "female" },
    { id: "zh-CN-XiaorouNeural", displayName: "Xiaorou", gender: "female" },
    { id: "zh-CN-XiaoruiNeural", displayName: "Xiaorui", gender: "female" },
    { id: "zh-CN-XiaoyanNeural", displayName: "Xiaoyan", gender: "female" },
    { id: "zh-CN-XiaozhenNeural", displayName: "Xiaozhen", gender: "female" },
    { id: "zh-CN-YunfengNeural", displayName: "Yunfeng", gender: "male" },
    { id: "zh-CN-YunhaoNeural", displayName: "Yunhao", gender: "male" },
    { id: "zh-CN-YunjieNeural", displayName: "Yunjie", gender: "male" },
    { id: "zh-CN-YunxiaNeural", displayName: "Yunxia", gender: "male" },
    { id: "zh-CN-YunyeNeural", displayName: "Yunye", gender: "male" },
    { id: "zh-CN-YunzeNeural", displayName: "Yunze", gender: "male" },
    {
      id: "zh-CN-liaoning-XiaobeiNeural",
      displayName: "Xiaobei (Liaoning)",
      gender: "female",
    },
    {
      id: "zh-CN-liaoning-YunbiaoNeural",
      displayName: "Yunbiao (Liaoning)",
      gender: "male",
    },
    {
      id: "zh-CN-shandong-YunxiangNeural",
      displayName: "Yunxiang (Shandong)",
      gender: "male",
    },
    {
      id: "zh-CN-sichuan-YunxiNeural",
      displayName: "Yunxi (Sichuan)",
      gender: "male",
    },
    {
      id: "zh-CN-henan-YundengNeural",
      displayName: "Yundeng (Henan)",
      gender: "male",
    },
    {
      id: "zh-CN-shaanxi-XiaoniNeural",
      displayName: "Xiaoni (Shaanxi)",
      gender: "female",
    },
    {
      id: "zh-CN-guangxi-YunqiNeural",
      displayName: "Yunqi (Guangxi)",
      gender: "male",
    },
  ],
  "zh-TW": [
    { id: "zh-TW-HsiaoChenNeural", displayName: "HsiaoChen", gender: "female" },
    { id: "zh-TW-YunJheNeural", displayName: "YunJhe", gender: "male" },
    { id: "zh-TW-HsiaoYuNeural", displayName: "HsiaoYu", gender: "female" },
  ],
  yue: [
    {
      id: "yue-CN-XiaoMinNeural",
      displayName: "XiaoMin (China)",
      gender: "female",
    },
    {
      id: "yue-CN-YunSongNeural",
      displayName: "YunSong (China)",
      gender: "male",
    },
    {
      id: "zh-HK-HiuMaanNeural",
      displayName: "HiuMaan (Hong Kong)",
      gender: "female",
    },
    {
      id: "zh-HK-WanLungNeural",
      displayName: "WanLung (Hong Kong)",
      gender: "male",
    },
    {
      id: "zh-HK-HiuGaaiNeural",
      displayName: "HiuGaai (Hong Kong)",
      gender: "female",
    },
  ],
  ar: [
    { id: "ar-EG-SalmaNeural", displayName: "Salma (Egypt)", gender: "female" },
    { id: "ar-EG-ShakirNeural", displayName: "Shakir (Egypt)", gender: "male" },
    {
      id: "ar-SA-ZariyahNeural",
      displayName: "Zariyah (Saudi Arabia)",
      gender: "female",
    },
    {
      id: "ar-SA-HamedNeural",
      displayName: "Hamed (Saudi Arabia)",
      gender: "male",
    },
    {
      id: "ar-AE-FatimaNeural",
      displayName: "Fatima (UAE)",
      gender: "female",
    },
    { id: "ar-AE-HamdanNeural", displayName: "Hamdan (UAE)", gender: "male" },
    {
      id: "ar-MA-MounaNeural",
      displayName: "Mouna (Morocco)",
      gender: "female",
    },
    {
      id: "ar-MA-JamalNeural",
      displayName: "Jamal (Morocco)",
      gender: "male",
    },
    {
      id: "ar-DZ-AminaNeural",
      displayName: "Amina (Algeria)",
      gender: "female",
    },
    {
      id: "ar-DZ-IsmaelNeural",
      displayName: "Ismael (Algeria)",
      gender: "male",
    },
    { id: "ar-IQ-RanaNeural", displayName: "Rana (Iraq)", gender: "female" },
    { id: "ar-IQ-BasselNeural", displayName: "Bassel (Iraq)", gender: "male" },
    {
      id: "ar-TN-ReemNeural",
      displayName: "Reem (Tunisia)",
      gender: "female",
    },
    { id: "ar-TN-HediNeural", displayName: "Hedi (Tunisia)", gender: "male" },
    {
      id: "ar-JO-SanaNeural",
      displayName: "Sana (Jordan)",
      gender: "female",
    },
    { id: "ar-JO-TaimNeural", displayName: "Taim (Jordan)", gender: "male" },
    {
      id: "ar-LB-LaylaNeural",
      displayName: "Layla (Lebanon)",
      gender: "female",
    },
    { id: "ar-LB-RamiNeural", displayName: "Rami (Lebanon)", gender: "male" },
    {
      id: "ar-KW-NouraNeural",
      displayName: "Noura (Kuwait)",
      gender: "female",
    },
    { id: "ar-KW-FahedNeural", displayName: "Fahed (Kuwait)", gender: "male" },
    {
      id: "ar-QA-AmalNeural",
      displayName: "Amal (Qatar)",
      gender: "female",
    },
    { id: "ar-QA-MoazNeural", displayName: "Moaz (Qatar)", gender: "male" },
    {
      id: "ar-BH-LailaNeural",
      displayName: "Laila (Bahrain)",
      gender: "female",
    },
    { id: "ar-BH-AliNeural", displayName: "Ali (Bahrain)", gender: "male" },
    {
      id: "ar-LY-ImanNeural",
      displayName: "Iman (Libya)",
      gender: "female",
    },
    { id: "ar-LY-OmarNeural", displayName: "Omar (Libya)", gender: "male" },
    {
      id: "ar-OM-AyshaNeural",
      displayName: "Aysha (Oman)",
      gender: "female",
    },
    {
      id: "ar-OM-AbdullahNeural",
      displayName: "Abdullah (Oman)",
      gender: "male",
    },
    {
      id: "ar-SY-AmanyNeural",
      displayName: "Amany (Syria)",
      gender: "female",
    },
    { id: "ar-SY-LaithNeural", displayName: "Laith (Syria)", gender: "male" },
    {
      id: "ar-YE-MaryamNeural",
      displayName: "Maryam (Yemen)",
      gender: "female",
    },
    { id: "ar-YE-SalehNeural", displayName: "Saleh (Yemen)", gender: "male" },
  ],
  cs: [
    { id: "cs-CZ-VlastaNeural", displayName: "Vlasta", gender: "female" },
    { id: "cs-CZ-AntoninNeural", displayName: "Antonín", gender: "male" },
  ],
  da: [
    { id: "da-DK-ChristelNeural", displayName: "Christel", gender: "female" },
    { id: "da-DK-JeppeNeural", displayName: "Jeppe", gender: "male" },
  ],
  fi: [
    { id: "fi-FI-SelmaNeural", displayName: "Selma", gender: "female" },
    { id: "fi-FI-HarriNeural", displayName: "Harri", gender: "male" },
    { id: "fi-FI-NooraNeural", displayName: "Noora", gender: "female" },
  ],
  el: [
    { id: "el-GR-AthinaNeural", displayName: "Athina", gender: "female" },
    { id: "el-GR-NestorasNeural", displayName: "Nestoras", gender: "male" },
  ],
  hi: [
    { id: "hi-IN-SwaraNeural", displayName: "Swara", gender: "female" },
    { id: "hi-IN-MadhurNeural", displayName: "Madhur", gender: "male" },
    { id: "hi-IN-AaravNeural", displayName: "Aarav", gender: "male" },
    { id: "hi-IN-AnanyaNeural", displayName: "Ananya", gender: "female" },
    { id: "hi-IN-AartiNeural", displayName: "Aarti", gender: "female" },
    { id: "hi-IN-ArjunNeural", displayName: "Arjun", gender: "male" },
    { id: "hi-IN-KavyaNeural", displayName: "Kavya", gender: "female" },
    { id: "hi-IN-KunalNeural", displayName: "Kunal", gender: "male" },
    { id: "hi-IN-RehaanNeural", displayName: "Rehaan", gender: "male" },
  ],
  hu: [
    { id: "hu-HU-NoemiNeural", displayName: "Noémi", gender: "female" },
    { id: "hu-HU-TamasNeural", displayName: "Tamás", gender: "male" },
  ],
  id: [
    { id: "id-ID-GadisNeural", displayName: "Gadis", gender: "female" },
    { id: "id-ID-ArdiNeural", displayName: "Ardi", gender: "male" },
  ],
  no: [
    { id: "nb-NO-PernilleNeural", displayName: "Pernille", gender: "female" },
    { id: "nb-NO-FinnNeural", displayName: "Finn", gender: "male" },
    { id: "nb-NO-IselinNeural", displayName: "Iselin", gender: "female" },
  ],
  ro: [
    { id: "ro-RO-AlinaNeural", displayName: "Alina", gender: "female" },
    { id: "ro-RO-EmilNeural", displayName: "Emil", gender: "male" },
  ],
  th: [
    { id: "th-TH-PremwadeeNeural", displayName: "Premwadee", gender: "female" },
    { id: "th-TH-NiwatNeural", displayName: "Niwat", gender: "male" },
    { id: "th-TH-AcharaNeural", displayName: "Achara", gender: "female" },
  ],
  uk: [
    { id: "uk-UA-PolinaNeural", displayName: "Polina", gender: "female" },
    { id: "uk-UA-OstapNeural", displayName: "Ostap", gender: "male" },
  ],
  vi: [
    { id: "vi-VN-HoaiMyNeural", displayName: "HoaiMy", gender: "female" },
    { id: "vi-VN-NamMinhNeural", displayName: "NamMinh", gender: "male" },
  ],
  af: [
    { id: "af-ZA-AdriNeural", displayName: "Adri", gender: "female" },
    { id: "af-ZA-WillemNeural", displayName: "Willem", gender: "male" },
  ],
  sq: [
    { id: "sq-AL-AnilaNeural", displayName: "Anila", gender: "female" },
    { id: "sq-AL-IlirNeural", displayName: "Ilir", gender: "male" },
  ],
  am: [
    { id: "am-ET-MekdesNeural", displayName: "Mekdes", gender: "female" },
    { id: "am-ET-AmehaNeural", displayName: "Ameha", gender: "male" },
  ],
  hy: [
    { id: "hy-AM-AnahitNeural", displayName: "Anahit", gender: "female" },
    { id: "hy-AM-HaykNeural", displayName: "Hayk", gender: "male" },
  ],
  as: [
    { id: "as-IN-YashicaNeural", displayName: "Yashica", gender: "female" },
    { id: "as-IN-PriyomNeural", displayName: "Priyom", gender: "male" },
  ],
  az: [
    { id: "az-AZ-BanuNeural", displayName: "Banu", gender: "female" },
    { id: "az-AZ-BabekNeural", displayName: "Babek", gender: "male" },
  ],
  eu: [
    { id: "eu-ES-AinhoaNeural", displayName: "Ainhoa", gender: "female" },
    { id: "eu-ES-AnderNeural", displayName: "Ander", gender: "male" },
  ],
  bn: [
    {
      id: "bn-BD-NabanitaNeural",
      displayName: "Nabanita (Bangladesh)",
      gender: "female",
    },
    {
      id: "bn-BD-PradeepNeural",
      displayName: "Pradeep (Bangladesh)",
      gender: "male",
    },
    {
      id: "bn-IN-TanishaaNeural",
      displayName: "Tanishaa (India)",
      gender: "female",
    },
    {
      id: "bn-IN-BashkarNeural",
      displayName: "Bashkar (India)",
      gender: "male",
    },
  ],
  bs: [
    { id: "bs-BA-VesnaNeural", displayName: "Vesna", gender: "female" },
    { id: "bs-BA-GoranNeural", displayName: "Goran", gender: "male" },
  ],
  bg: [
    { id: "bg-BG-KalinaNeural", displayName: "Kalina", gender: "female" },
    { id: "bg-BG-BorislavNeural", displayName: "Borislav", gender: "male" },
  ],
  my: [
    { id: "my-MM-NilarNeural", displayName: "Nilar", gender: "female" },
    { id: "my-MM-ThihaNeural", displayName: "Thiha", gender: "male" },
  ],
  ca: [
    { id: "ca-ES-JoanaNeural", displayName: "Joana", gender: "female" },
    { id: "ca-ES-EnricNeural", displayName: "Enric", gender: "male" },
    { id: "ca-ES-AlbaNeural", displayName: "Alba", gender: "female" },
  ],
  hr: [
    { id: "hr-HR-GabrijelaNeural", displayName: "Gabrijela", gender: "female" },
    { id: "hr-HR-SreckoNeural", displayName: "Srećko", gender: "male" },
  ],
  et: [
    { id: "et-EE-AnuNeural", displayName: "Anu", gender: "female" },
    { id: "et-EE-KertNeural", displayName: "Kert", gender: "male" },
  ],
  tl: [
    { id: "fil-PH-BlessicaNeural", displayName: "Blessica", gender: "female" },
    { id: "fil-PH-AngeloNeural", displayName: "Angelo", gender: "male" },
  ],
  gl: [
    { id: "gl-ES-SabelaNeural", displayName: "Sabela", gender: "female" },
    { id: "gl-ES-RoiNeural", displayName: "Roi", gender: "male" },
  ],
  ka: [
    { id: "ka-GE-EkaNeural", displayName: "Eka", gender: "female" },
    { id: "ka-GE-GiorgiNeural", displayName: "Giorgi", gender: "male" },
  ],
  gu: [
    { id: "gu-IN-DhwaniNeural", displayName: "Dhwani", gender: "female" },
    { id: "gu-IN-NiranjanNeural", displayName: "Niranjan", gender: "male" },
  ],
  iw: [
    { id: "he-IL-HilaNeural", displayName: "Hila", gender: "female" },
    { id: "he-IL-AvriNeural", displayName: "Avri", gender: "male" },
  ],
  is: [
    { id: "is-IS-GudrunNeural", displayName: "Guðrún", gender: "female" },
    { id: "is-IS-GunnarNeural", displayName: "Gunnar", gender: "male" },
  ],
  ga: [
    { id: "ga-IE-OrlaNeural", displayName: "Orla", gender: "female" },
    { id: "ga-IE-ColmNeural", displayName: "Colm", gender: "male" },
  ],
  jv: [
    { id: "jv-ID-SitiNeural", displayName: "Siti", gender: "female" },
    { id: "jv-ID-DimasNeural", displayName: "Dimas", gender: "male" },
  ],
  kn: [
    { id: "kn-IN-SapnaNeural", displayName: "Sapna", gender: "female" },
    { id: "kn-IN-GaganNeural", displayName: "Gagan", gender: "male" },
  ],
  kk: [
    { id: "kk-KZ-AigulNeural", displayName: "Aigul", gender: "female" },
    { id: "kk-KZ-DauletNeural", displayName: "Daulet", gender: "male" },
  ],
  km: [
    { id: "km-KH-SreymomNeural", displayName: "Sreymom", gender: "female" },
    { id: "km-KH-PisethNeural", displayName: "Piseth", gender: "male" },
  ],
  lo: [
    { id: "lo-LA-KeomanyNeural", displayName: "Keomany", gender: "female" },
    {
      id: "lo-LA-ChanthavongNeural",
      displayName: "Chanthavong",
      gender: "male",
    },
  ],
  lv: [
    { id: "lv-LV-EveritaNeural", displayName: "Everita", gender: "female" },
    { id: "lv-LV-NilsNeural", displayName: "Nils", gender: "male" },
  ],
  lt: [
    { id: "lt-LT-OnaNeural", displayName: "Ona", gender: "female" },
    { id: "lt-LT-LeonasNeural", displayName: "Leonas", gender: "male" },
  ],
  mk: [
    { id: "mk-MK-MarijaNeural", displayName: "Marija", gender: "female" },
    {
      id: "mk-MK-AleksandarNeural",
      displayName: "Aleksandar",
      gender: "male",
    },
  ],
  ms: [
    { id: "ms-MY-YasminNeural", displayName: "Yasmin", gender: "female" },
    { id: "ms-MY-OsmanNeural", displayName: "Osman", gender: "male" },
  ],
  ml: [
    { id: "ml-IN-SobhanaNeural", displayName: "Sobhana", gender: "female" },
    { id: "ml-IN-MidhunNeural", displayName: "Midhun", gender: "male" },
  ],
  mt: [
    { id: "mt-MT-GraceNeural", displayName: "Grace", gender: "female" },
    { id: "mt-MT-JosephNeural", displayName: "Joseph", gender: "male" },
  ],
  mr: [
    { id: "mr-IN-AarohiNeural", displayName: "Aarohi", gender: "female" },
    { id: "mr-IN-ManoharNeural", displayName: "Manohar", gender: "male" },
  ],
  mn: [
    { id: "mn-MN-YesuiNeural", displayName: "Yesui", gender: "female" },
    { id: "mn-MN-BataaNeural", displayName: "Bataa", gender: "male" },
  ],
  ne: [
    { id: "ne-NP-HemkalaNeural", displayName: "Hemkala", gender: "female" },
    { id: "ne-NP-SagarNeural", displayName: "Sagar", gender: "male" },
  ],
  or: [
    { id: "or-IN-SubhasiniNeural", displayName: "Subhasini", gender: "female" },
    { id: "or-IN-SukantNeural", displayName: "Sukant", gender: "male" },
  ],
  ps: [
    { id: "ps-AF-LatifaNeural", displayName: "Latifa", gender: "female" },
    { id: "ps-AF-GulNawazNeural", displayName: "GulNawaz", gender: "male" },
  ],
  fa: [
    { id: "fa-IR-DilaraNeural", displayName: "Dilara", gender: "female" },
    { id: "fa-IR-FaridNeural", displayName: "Farid", gender: "male" },
  ],
  pa: [
    { id: "pa-IN-OjasNeural", displayName: "Ojas", gender: "male" },
    { id: "pa-IN-VaaniNeural", displayName: "Vaani", gender: "female" },
  ],
  // Serbian is written in both Cyrillic and Latin script, and Azure ships a
  // separate voice per script for each speaker. The Cyrillic ids stay the
  // catalog identity (stored prefs, picker rows); the server switches to the
  // Latin twin per synthesis request when the text is Latin.
  sr: [
    {
      id: "sr-RS-SophieNeural",
      displayName: "Sophie",
      gender: "female",
      latinScriptId: "sr-Latn-RS-SophieNeural",
    },
    {
      id: "sr-RS-NicholasNeural",
      displayName: "Nicholas",
      gender: "male",
      latinScriptId: "sr-Latn-RS-NicholasNeural",
    },
  ],
  si: [
    { id: "si-LK-ThiliniNeural", displayName: "Thilini", gender: "female" },
    { id: "si-LK-SameeraNeural", displayName: "Sameera", gender: "male" },
  ],
  sk: [
    { id: "sk-SK-ViktoriaNeural", displayName: "Viktória", gender: "female" },
    { id: "sk-SK-LukasNeural", displayName: "Lukáš", gender: "male" },
  ],
  sl: [
    { id: "sl-SI-PetraNeural", displayName: "Petra", gender: "female" },
    { id: "sl-SI-RokNeural", displayName: "Rok", gender: "male" },
  ],
  so: [
    { id: "so-SO-UbaxNeural", displayName: "Ubax", gender: "female" },
    { id: "so-SO-MuuseNeural", displayName: "Muuse", gender: "male" },
  ],
  su: [
    { id: "su-ID-TutiNeural", displayName: "Tuti", gender: "female" },
    { id: "su-ID-JajangNeural", displayName: "Jajang", gender: "male" },
  ],
  sw: [
    { id: "sw-KE-ZuriNeural", displayName: "Zuri (Kenya)", gender: "female" },
    {
      id: "sw-KE-RafikiNeural",
      displayName: "Rafiki (Kenya)",
      gender: "male",
    },
    {
      id: "sw-TZ-RehemaNeural",
      displayName: "Rehema (Tanzania)",
      gender: "female",
    },
    {
      id: "sw-TZ-DaudiNeural",
      displayName: "Daudi (Tanzania)",
      gender: "male",
    },
  ],
  ta: [
    {
      id: "ta-IN-PallaviNeural",
      displayName: "Pallavi (India)",
      gender: "female",
    },
    {
      id: "ta-IN-ValluvarNeural",
      displayName: "Valluvar (India)",
      gender: "male",
    },
    {
      id: "ta-LK-SaranyaNeural",
      displayName: "Saranya (Sri Lanka)",
      gender: "female",
    },
    {
      id: "ta-LK-KumarNeural",
      displayName: "Kumar (Sri Lanka)",
      gender: "male",
    },
    {
      id: "ta-MY-KaniNeural",
      displayName: "Kani (Malaysia)",
      gender: "female",
    },
    {
      id: "ta-MY-SuryaNeural",
      displayName: "Surya (Malaysia)",
      gender: "male",
    },
    {
      id: "ta-SG-VenbaNeural",
      displayName: "Venba (Singapore)",
      gender: "female",
    },
    {
      id: "ta-SG-AnbuNeural",
      displayName: "Anbu (Singapore)",
      gender: "male",
    },
  ],
  te: [
    { id: "te-IN-ShrutiNeural", displayName: "Shruti", gender: "female" },
    { id: "te-IN-MohanNeural", displayName: "Mohan", gender: "male" },
  ],
  ur: [
    { id: "ur-IN-GulNeural", displayName: "Gul (India)", gender: "female" },
    { id: "ur-IN-SalmanNeural", displayName: "Salman (India)", gender: "male" },
    {
      id: "ur-PK-UzmaNeural",
      displayName: "Uzma (Pakistan)",
      gender: "female",
    },
    { id: "ur-PK-AsadNeural", displayName: "Asad (Pakistan)", gender: "male" },
  ],
  uz: [
    { id: "uz-UZ-MadinaNeural", displayName: "Madina", gender: "female" },
    { id: "uz-UZ-SardorNeural", displayName: "Sardor", gender: "male" },
  ],
  cy: [
    { id: "cy-GB-NiaNeural", displayName: "Nia", gender: "female" },
    { id: "cy-GB-AledNeural", displayName: "Aled", gender: "male" },
  ],
  zu: [
    { id: "zu-ZA-ThandoNeural", displayName: "Thando", gender: "female" },
    { id: "zu-ZA-ThembaNeural", displayName: "Themba", gender: "male" },
  ],
};

/**
 * Returns true if there are any Azure voices available for the given
 * language — equivalently, whether the server can synthesize Azure audio
 * for it. The picker and `canUseAzure` gating both key off this.
 *
 * @param {string} language
 * @returns {boolean}
 */
export const isAzureSupportedLanguage = (language) =>
  (azureVoicesByLanguage[language] || []).length > 0;

/**
 * Returns the voice entry matching `voiceId` for `language`, or undefined.
 * Used by the server to validate voice ids coming from the client, and by
 * the client to look up display metadata for a stored Azure pref.
 *
 * @param {string} language
 * @param {string} voiceId
 * @returns {AzureVoice | undefined}
 */
export const findAzureVoice = (language, voiceId) =>
  (azureVoicesByLanguage[language] || []).find((voice) => voice.id === voiceId);

/**
 * Returns the default Azure voice for `language` — the first catalog entry,
 * which is also the voice the server uses when `/api/tts/synthesize` is
 * called without a `voiceId` (subject to the timezone-based regional
 * overrides in `src/msSpeech.js` for `es` and `pt`).
 *
 * @param {string} language
 * @returns {AzureVoice | undefined}
 */
export const getDefaultAzureVoice = (language) =>
  (azureVoicesByLanguage[language] || [])[0];
